/**
 * persistProfilePref — Feb 28, 2026
 *
 * Single helper that fire-and-forget PUTs a single Profile/Settings
 * preference to /api/auth/profile so the user's setting follows them
 * across devices. Used by every Profile/Settings toggle that previously
 * only wrote to localStorage.
 *
 * Why a helper instead of a hook? Many of these call-sites live inside
 * inline `onChange` handlers (Modals.jsx toggles, view-mode buttons,
 * the FormatLockPill popover, the floating mic FAB, etc.). A simple
 * function we can drop alongside the existing `localStorage.setItem`
 * line keeps the diff minimal and the refactor surface tiny.
 *
 * Usage:
 *
 *   import { persistProfilePref } from '../../utils/persistProfilePref';
 *   localStorage.setItem('writeright_demo_mode', '1');
 *   persistProfilePref({ demo_mode: true });
 *
 * Guest mode: the underlying axios.put is rejected with 401 by the
 * backend. We swallow that silently so guests can still flip toggles
 * without a network error in the console. The local-only behavior
 * remains unchanged.
 */
import axios from 'axios';
import { announceProfileSaved } from '../components/mediscribe/ProfileSyncBadge';

let _inflightTimer = null;
let _pendingPayload = {};

/**
 * Coalesce rapid successive preference updates into a single PUT so
 * something like a drag-to-reorder header doesn't fire 8 calls in 200ms.
 * Debounce is 350ms — fast enough to feel instant on a single toggle,
 * slow enough to batch a drag.
 */
export const persistProfilePref = (patch, { announce = true } = {}) => {
  if (!patch || typeof patch !== 'object') return;
  const API = window.__WR_API_BASE__;
  if (!API) return;
  _pendingPayload = { ..._pendingPayload, ...patch };
  if (_inflightTimer) clearTimeout(_inflightTimer);
  _inflightTimer = setTimeout(() => {
    const body = _pendingPayload;
    _pendingPayload = {};
    _inflightTimer = null;
    axios.put(`${API}/api/auth/profile`, body, { withCredentials: true })
      .then(() => {
        if (announce) {
          try { announceProfileSaved(true); } catch { /* ignore */ }
        }
      })
      .catch((err) => {
        // 401 = guest mode (no session) — silently ignore.
        const status = err?.response?.status;
        if (status && status !== 401 && status >= 400) {
          console.warn('persistProfilePref save failed:', body, err.message);
          if (announce) {
            try { announceProfileSaved(false); } catch { /* ignore */ }
          }
        }
      });
  }, 350);
};

export default persistProfilePref;
